import { apiClient } from '@/api/client';
import type { LookupItem } from '@/types/shared';

export interface LookupListParams {
  /** Defaults server-side to `true` (active-only) when omitted. Pass `false` to also fetch
   * inactive values — used by the register/edit forms so a previously-selected-but-now-inactive
   * value can still be shown (greyed, forced re-selection) per 10-design-system.md §4.3, and by
   * the Settings → Lookups management screen so Admins can see and reactivate inactive values. */
  activeOnly?: boolean;
}

async function listLookups(path: string, params?: LookupListParams): Promise<LookupItem[]> {
  const { data } = await apiClient.get<LookupItem[]>(path, {
    params: params?.activeOnly === undefined ? undefined : { activeOnly: params.activeOnly },
  });
  return data;
}

/** GET /lookups/grades */
export function getGrades(params?: LookupListParams): Promise<LookupItem[]> {
  return listLookups('/lookups/grades', params);
}

/** GET /lookups/schools */
export function getSchools(params?: LookupListParams): Promise<LookupItem[]> {
  return listLookups('/lookups/schools', params);
}

/** GET /lookups/educational-administrations */
export function getEducationalAdministrations(params?: LookupListParams): Promise<LookupItem[]> {
  return listLookups('/lookups/educational-administrations', params);
}

/** GET /lookups/referral-partners — T145/T148 (docs/architecture/16-referral-partners.md §7 #1).
 * Name only, no money; open to any authenticated role (student form picker, T152). */
export function getReferralPartners(params?: LookupListParams): Promise<LookupItem[]> {
  return listLookups('/lookups/referral-partners', params);
}

/** GET /lookups/branches — T163/T174 (docs/design/19-branch-scoping-ux.md §1.2). The FIRST
 * branch-scoped lookup: a non-all-branches caller only receives their own assigned branches, so the
 * result legitimately differs per user. Used by the Branches settings tab (Admin, always sees every
 * branch) and by the user branch-assignment checklist (`UserFormPanel.tsx`, also Admin-only so this
 * distinction is moot there too — see the doc's own note). Never client-filter this response further
 * — the server has already scoped it. */
export function getBranches(params?: LookupListParams): Promise<LookupItem[]> {
  return listLookups('/lookups/branches', params);
}

/**
 * The five identically-shaped reference lists managed from Settings → Lookups
 * (`grades.controller.ts` / `schools.controller.ts` / `educational-administrations.controller.ts` /
 * `referral-partners.controller.ts` / `branches.controller.ts`, all mounted under
 * `LookupCrudService` — see `apps/api/src/modules/lookups`). `referral-partners` was the 4th
 * resource (T145/T148/T151); `branches` is the 5th (T163/T174, docs/design/19-branch-scoping-ux.md
 * §1) — same CRUD shape, reused verbatim by `LookupManager`.
 */
export type LookupResourceKey =
  | 'grades'
  | 'schools'
  | 'educational-administrations'
  | 'referral-partners'
  | 'branches';

const RESOURCE_PATHS: Record<LookupResourceKey, string> = {
  grades: '/lookups/grades',
  schools: '/lookups/schools',
  'educational-administrations': '/lookups/educational-administrations',
  'referral-partners': '/lookups/referral-partners',
  branches: '/lookups/branches',
};

/** Mirrors `LookupUpsertDto` (backend) — `labelAr`/`labelEn` always required by the server on both
 * create and rename; `isActive` is optional and, on PATCH, left unchanged when omitted. */
export interface LookupUpsertPayload {
  labelAr: string;
  labelEn: string;
  isActive?: boolean;
}

/** GET /lookups/{resource} — used by the management screen to list *all* values (active +
 * inactive) for a given resource, keyed the same way the Admin picks a tab. */
export function listLookupResource(
  resource: LookupResourceKey,
  params?: LookupListParams,
): Promise<LookupItem[]> {
  return listLookups(RESOURCE_PATHS[resource], params);
}

/** POST /lookups/{resource} — Admin-only (RoleGuard on the route + `@Roles('Admin')` server-side). */
export async function createLookupValue(
  resource: LookupResourceKey,
  payload: LookupUpsertPayload,
): Promise<LookupItem> {
  const { data } = await apiClient.post<LookupItem>(RESOURCE_PATHS[resource], payload);
  return data;
}

/** PATCH /lookups/{resource}/{id} — renames `labelAr`/`labelEn` and/or flips `isActive` (used both
 * for "rename" and for reactivating a previously-deactivated value, since the DELETE verb below
 * only ever sets `isActive: false`). Admin-only. */
export async function updateLookupValue(
  resource: LookupResourceKey,
  id: number,
  payload: LookupUpsertPayload,
): Promise<LookupItem> {
  const { data } = await apiClient.patch<LookupItem>(`${RESOURCE_PATHS[resource]}/${id}`, payload);
  return data;
}

/** DELETE /lookups/{resource}/{id} — soft "deactivate" (sets `isActive: false`server-side; the row
 * is never actually removed — `LookupCrudService.deactivate()`). Admin-only. */
export async function deactivateLookupValue(
  resource: LookupResourceKey,
  id: number,
): Promise<void> {
  await apiClient.delete(`${RESOURCE_PATHS[resource]}/${id}`);
}
